/**
 * Auth Redux Slice
 * 
 * Manages authentication and authorization state including user profile,
 * permissions, and authentication status.
 */

import Account from '@/features/account/application/domain/entities/account';
import { AppUser } from '@/features/app-user/application/domain/entities/account';
import { AuthState, Permission } from '@/shared/application/types/authTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  account: null,
  role: null,
  permissions: [],
  isAuthenticated: false,
  hasSelectedAccount: false,
  loading: false,
  error: null,
  isRemoteI9User: false,
};

// ============================================================================
// Slice Definition
// ============================================================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Log out the user and clear all auth state
     */
    logout: (state) => {
      state.user = null;
      state.account = null;
      state.role = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.hasSelectedAccount = false;
      state.loading = false;
      state.error = null;
      state.isRemoteI9User = false;
    },

    /**
     * Clear any error messages
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Manually set permissions (useful for testing or admin overrides)
     */
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
    },

    /**
     *  set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    /**
     * set error state
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    /**
     * set user state
     */
    setUser: (state, action: PayloadAction<AppUser | null>) => {
      state.user = action.payload;
    },

    /**
     * set account state
     */
    setAccount: (state, action: PayloadAction<Account | null>) => {
      state.account = action.payload;
    },

    /**
     * set role state
     */
    setRole: (state, action: PayloadAction<string | null>) => {
      state.role = action.payload;
    },

    /**
     * set is authenticated state
     */
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },

    /**
     * set whether user has selected an account/organization
     */
    setHasSelectedAccount: (state, action: PayloadAction<boolean>) => {
      state.hasSelectedAccount = action.payload;
    },

    setIsRemoteI9User: (state, action: PayloadAction<boolean>) => {
      state.isRemoteI9User = action.payload;
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  logout,
  clearError,
  setPermissions,
  setLoading,
  setError,
  setUser,
  setAccount,
  setRole,
  setIsAuthenticated,
  setHasSelectedAccount,
  setIsRemoteI9User,
} = authSlice.actions;
export default authSlice.reducer;
